import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MutationOptions, MutationOptionsWithResponse } from '../../../services/general.types';
import { useTransactionService } from '../../../services/transaction.service';
import { TransactionTypeValue } from '../../../services/transaction.types';
import { QUERY_KEYS } from '../../../utils/queryKeys';

interface NotSpamParams {
    contractAddress: string;
    platformIdentifier: string;
    blacklistId: number;
}

interface SpamParams {
    contractAddress: string;
    platformIdentifier: string;
    coin: string;
}

interface NonTaxableParams {
    id: number;
    targetTypeId: TransactionTypeValue;
}

interface GroupSaveParams {
    ids: number[];
    typeId: number;
}

export const useTransactionMutations = () => {
    const queryClient = useQueryClient();
    const transactionService = useTransactionService();

    const clearAIAnalysisCaches = () => {
        // Clear all AI analysis caches from sessionStorage
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('ai-analysis-')) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
        if (keysToRemove.length > 0) {
            console.log(`[Transaction Mutations] Cleared ${keysToRemove.length} AI analysis cache(s)`);
        }
    };

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['transactions'] });
        queryClient.invalidateQueries({ queryKey: ['accounts'] });
        queryClient.invalidateQueries({ queryKey: ['liquidityPools'] });
        queryClient.invalidateQueries({ queryKey: ['spamCoins'] });
        queryClient.invalidateQueries({ queryKey: ['recalculateInfo'] });
        queryClient.invalidateQueries({ queryKey: ['userTypes'] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ALL_TIME_ISSUES] });

        // Clear AI analysis caches since transaction data has changed
        clearAIAnalysisCaches();
    };

    const invalidateRecalcQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['recalculateInfo'] });
    };

    const { mutate: baseSaveTransaction, isPending: isSaving } = useMutation({
        mutationKey: ['saveTransaction'],
        mutationFn: transactionService.save,
        onSuccess: () => {
            invalidateQueries();
        },
        onError: (error) => {
            console.error('Error saving transaction:', error);
            invalidateQueries();
        },
    });

    const { mutate: baseDeleteTransactions, isPending: isDeleting } = useMutation({
        mutationKey: ['deleteTransactions'],
        mutationFn: (ids: number[]) => transactionService.deleteAllByIds(ids),
        onSuccess: () => {
            invalidateQueries();
        },
        onError: (error) => {
            console.error('Error deleting transaction:', error);
            invalidateQueries();
        },
    });

    const { mutate: baseMarkSpam } = useMutation({
        mutationKey: ['markSpam'],
        mutationFn: (params: SpamParams[]) => transactionService.groupMarkSpam(params),
        onSuccess: () => {
            invalidateQueries();
        },
        onError: (error) => {
            console.error('Error marking transactions as spam:', error);
            invalidateQueries();
        },
    });

    const { mutate: baseMarkNotSpam } = useMutation({
        mutationKey: ['markNotSpam'],
        mutationFn: (params: NotSpamParams[]) => transactionService.groupRemoveSpam(params),
        onSuccess: () => {
            invalidateQueries();
        },
        onError: (error) => {
            console.error('Error marking transactions as not spam:', error);
            invalidateQueries();
        },
    });

    const { mutate: baseMarkNonTaxable } = useMutation({
        mutationKey: ['markNonTaxable'],
        mutationFn: (params: NonTaxableParams[]) => transactionService.groupMarkNonTaxable(params),
        onSuccess: () => {
            invalidateQueries();
        },
        onError: (error) => {
            console.error('Error marking transactions as non-taxable:', error);
            invalidateQueries();
        },
    });

    const { mutate: baseGroupSave } = useMutation({
        mutationKey: ['groupSave'],
        mutationFn: (params: GroupSaveParams) => transactionService.groupSave(params.ids, { typeId: params.typeId }),
        onSuccess: () => {
            invalidateQueries();
        },
        onError: (error) => {
            console.error('Error updating transaction types:', error);
            invalidateQueries();
        },
    });

    // Add recalculation mutations
    const { mutate: baseRecalculateStart, isPending: isRecalculateStarting } = useMutation({
        mutationKey: ['recalculateStart'],
        mutationFn: transactionService.recalculateStart,
        onSuccess: () => {
            invalidateRecalcQueries();
        },
        onError: (error) => {
            console.error('Error starting recalculation:', error);
        },
    });

    const { mutate: baseRecalculateStop, isPending: isRecalculateStopping } = useMutation({
        mutationKey: ['recalculateStop'],
        mutationFn: transactionService.recalculateStop,
        onSuccess: () => {
            invalidateRecalcQueries();
        },
        onError: (error) => {
            console.error('Error stopping recalculation:', error);
        },
    });

    // Add recalculateInfo query mutation
    const { mutate: baseRecalculateInfo, isPending: isRecalculateInfoLoading } = useMutation({
        mutationKey: ['recalculateInfo'],
        mutationFn: transactionService.recalculateInfo,
        onSuccess: () => {},
        onError: (error) => {
            console.error('Error fetching recalculation info:', error);
        },
    });

    const saveTransaction = (data: Parameters<typeof baseSaveTransaction>[0], options?: MutationOptions) => {
        baseSaveTransaction(data, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const deleteTransactions = (ids: number[], options?: MutationOptions) => {
        baseDeleteTransactions(ids, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const markTransactionsAsSpam = (params: SpamParams[], options?: MutationOptions) => {
        baseMarkSpam(params, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const markTransactionsAsNotSpam = (params: NotSpamParams[], options?: MutationOptions) => {
        baseMarkNotSpam(params, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const markNonTaxable = (params: NonTaxableParams[], options?: MutationOptions) => {
        baseMarkNonTaxable(params, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const groupSaveTransaction = (params: GroupSaveParams, options?: MutationOptions) => {
        baseGroupSave(params, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const startRecalculation = (options?: MutationOptions) => {
        baseRecalculateStart(undefined, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const stopRecalculation = (options?: MutationOptions) => {
        baseRecalculateStop(undefined, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    const getRecalculationInfo = (options?: MutationOptionsWithResponse) => {
        baseRecalculateInfo(undefined, {
            onSuccess: (data) => {
                options?.onSuccess?.(data);
            },
            onError: options?.onError,
        });
    };

    return {
        saveTransaction,
        deleteTransactions,
        markTransactionsAsSpam,
        markTransactionsAsNotSpam,
        markNonTaxable,
        groupSaveTransaction,
        startRecalculation,
        stopRecalculation,
        getRecalculationInfo,
        isSaving,
        isDeleting,
        isRecalculateStarting,
        isRecalculateStopping,
        isRecalculateInfoLoading,
    };
};
